/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Box,
  Flex,
  Text,
  Divider,
  Spinner,
  ThemeProvider,
  getThemeByName,
  Input,
} from '@tishman/components';
import React, { Fragment, useContext, useState, useEffect } from 'react';
import { CalendarArrow } from '@tishman/icons';

import {
  COMP_TICKETS,
  Flow,
  StatGroup,
} from '../../../services/viva/constants';
import { formatMoney } from '../../../utils';
import { Translatable, useTranslations } from '../../../utils/use-translations';
import { SelectionBlock } from '../../../blocks/Checkout/Summary/SelectionBlock';
import {
  actions,
  selectCheckoutCartAddonSelections,
  useAppSelector,
  useAppDispatch,
  selectCheckoutCartItems,
  selectAccountZipCode,
} from '../../../store';
import { ConfirmationPageContext } from '../../../utils/confirmation-page-context-provider';
import { parseFloatToDollars } from '../../../utils/parse-float-to-dollars';
import { useValidateCouponQuery } from '../../../services/viva';
import { useCheckBasketForSummary } from '../../../utils/use-check-basket-for-summary';
import { isBosError, putInArray } from '../../../services/viva/utils';
import {
  isLocalCoupon,
  isLocalZipCode,
} from '../../../utils/coupons-by-zipcode';
import {
  getLocalStoragePromoCodeParam,
  setLocalStoragePromoCodeParam,
} from '../../../utils/use-promo-code-param';

import type { ShopCartSelection, CheckoutTotals } from '../../../store/types';
import type { CheckoutTranslationsForLocale } from '../../../data/translations/checkout';
import type { CartTranslations } from '../../../data/translations/cart';

const spaceBetween = { justifyContent: 'space-between' };

export type CartSelectionsByDate = { [key in Flow]?: ShopCartSelection[][] };

type Props = {
  isLoading: boolean;
  isOrderConfirmed?: boolean;
  flow: Flow;
  totals?: CheckoutTotals;
  cartSelectionsByDate: CartSelectionsByDate;
};

export function OrderSummaryViva({
  flow,
  totals,
  cartSelectionsByDate,
  isLoading,
  isOrderConfirmed,
}: Props) {
  const translations = useTranslations<CartTranslations>(Translatable.Cart);
  const checkoutTranslations = useTranslations<CheckoutTranslationsForLocale>(
    Translatable.Checkout,
  );
  const { checkout, cartSelectionsByDate: confirmationCartSelections } =
    useContext(ConfirmationPageContext);

  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');

  const cartAddOns = useAppSelector(selectCheckoutCartAddonSelections);
  const dispatch = useAppDispatch();
  const validateCoupon = useValidateCouponQuery();
  const { checkBasket, error: checkBasketError } = useCheckBasketForSummary({
    setIsLoading: setIsCouponLoading,
  });

  const checkoutItems = useAppSelector(selectCheckoutCartItems).map((e) => {
    return { AK: e.AK, QTY: e.QTY };
  });

  const zipCode = useAppSelector(selectAccountZipCode);

  // Apply promo code from local storage on component mount
  useEffect(() => {
    const storedPromoCode = getLocalStoragePromoCodeParam();
    if (storedPromoCode && !isOrderConfirmed) {
      setCouponCode(storedPromoCode);
      applyCouponHandler(storedPromoCode);
    }
  }, []);

  const applyCouponHandler = async (
    codeOrEvent?: string | React.MouseEvent,
  ) => {
    const promoCode =
      typeof codeOrEvent === 'string' ? codeOrEvent : couponCode;
    if (promoCode === '') return;

    const handleCouponError = (message: string) => {
      setCouponError(message);
      setIsCouponLoading(false);
    };

    setIsCouponLoading(true);
    setCouponError('');
    dispatch(
      actions.checkout.setCheckoutTotals({
        ...totals,
      }),
    );

    const couponResponse = await validateCoupon.mutateAsync({
      coupon: promoCode,
    });
    const isError = isBosError(couponResponse);

    // AND check couponResponse for successful code
    if (!isError) {
      const {
        COUPON: {
          CODE: validCoupon,
          RULELIST: { RULE },
        },
      } = couponResponse;

      const rules = putInArray(RULE);

      // Collect all applicable products and find min/max across rules
      const applicableProductsList = rules.reduce((products, rule) => {
        const choices = putInArray(rule.CHOICELIST.CHOICE);
        return [...products, ...choices];
      }, [] as { PRODUCTAK: string }[]);

      // Use the most permissive min/max across rules
      const minCouponsRequired = Math.min(
        ...rules.map((rule) => parseInt(rule.MIN)),
      );
      const maxCouponsAllowed = rules.reduce(
        (sum, rule) => sum + parseInt(rule.MAX),
        0,
      );

      const totalApplicableProducts = checkoutItems.reduce((sum, item) => {
        const isApplicable = applicableProductsList.some(
          (p: { PRODUCTAK: string }) => p.PRODUCTAK === item.AK,
        );
        return sum + (isApplicable ? item.QTY || 0 : 0);
      }, 0);

      let quantityError = '';
      // Check that each rule has at least one matching product in its choices
      const isCouponApplicable = rules.every((rule) => {
        const choices = putInArray(rule.CHOICELIST.CHOICE);
        // For this rule, check if any of our cart items match any of its choices
        const hasMatchingProduct = choices.some(
          (choice: { PRODUCTAK: string }) => {
            return checkoutItems.some((item) => item.AK === choice.PRODUCTAK);
          },
        );

        if (!hasMatchingProduct) return false;

        if (totalApplicableProducts < minCouponsRequired) {
          quantityError = `This promo code requires a minimum of ${minCouponsRequired} tickets`;
          return false;
        }
        if (totalApplicableProducts > maxCouponsAllowed) {
          quantityError = `This promo code allows a maximum of ${maxCouponsAllowed} tickets`;
          return false;
        }

        return true;
      });

      if (isLocalCoupon(promoCode)) {
        if (!zipCode) {
          handleCouponError("Please enter your card's zip code and try again.");
          return;
        }
        if (!zipCode || !isLocalZipCode(zipCode)) {
          handleCouponError(`"${promoCode}" cannot be applied to this order.`);
          return;
        }
      }

      const processCouponValidation = async (couponCode: string) => {
        try {
          const couponResponse = await checkBasket(couponCode);

          if (isBosError(couponResponse)) {
            if (couponResponse.ERROR.CODE === '4107') {
              handleCouponError(
                `"${couponCode}" is not valid during the selected date and time.`,
              );
              return false;
            }

            handleCouponError(
              `"${couponCode}" cannot be applied to this order. Please enter a valid code.`,
            );
            return false;
          }
          setCouponCode('');
          // Clear the promo code from local storage after successful application
          setLocalStoragePromoCodeParam('');
          return true;
        } catch (error) {
          console.log({ couponError: error });
          handleCouponError('An error has occurred. Please try again.');
          return false;
        }
      };

      if (!validCoupon || !isCouponApplicable) {
        handleCouponError(
          quantityError ||
            `"${promoCode}" cannot be applied to this order. Please enter a valid code.`,
        );
        return;
      }

      await processCouponValidation(validCoupon);
    } else {
      handleCouponError(
        `"${promoCode}" cannot be applied to this order. Please enter a valid code.`,
      );
    }
  };

  if (!cartSelectionsByDate || !confirmationCartSelections) return null;
  if (!totals || !translations) return null;

  if (flow === Flow.CITY_PASS) {
    (
      cartSelectionsByDate[Flow.CITY_PASS] ??
      confirmationCartSelections[Flow.CITY_PASS]
    )?.sort((a, b) => {
      if (a[0].label < b[0].label) {
        return -1;
      } else if (a[0].label > b[0].label) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  return (
    <Box sx={{ position: 'relative', height: 'fit-content' }}>
      {(isLoading || isCouponLoading) && (
        <Flex
          sx={{
            height: '100%',
            width: '100%',
            position: 'absolute',
            left: 0,
            top: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
          }}
        >
          <Spinner
            sx={{
              display: 'block',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          />
        </Flex>
      )}
      <Text sx={{ mt: [0, 4], fontSize: 6, fontWeight: 500 }}>
        Order Summary
      </Text>
      <Flex
        sx={{
          flexDirection: 'column',
          rowGap: 40,
        }}
      >
        {Object.keys(cartSelectionsByDate).map((_key) => {
          const flow = _key as Flow;

          return (
            cartSelectionsByDate[flow] ?? confirmationCartSelections[flow]
          )?.map((byDate) => {
            const addOns =
              cartAddOns[flow]?.[byDate[0]?.dateTime ?? 0] ??
              checkout.cart.addOnSelections[flow]?.[byDate[0]?.dateTime ?? 0];

            return (
              <Flex
                key={flow}
                sx={{
                  flexDirection: 'column',
                  rowGap: 3,
                }}
              >
                <Text
                  sx={{
                    fontSize: [21],
                    fontWeight: [400],
                  }}
                >
                  {checkoutTranslations?.forFlow[flow]?.title.replace(
                    /redeem your/i,
                    '',
                  )}
                </Text>
                {byDate.map((selection) => {
                  const selectionPrice = selection.price * selection.count;

                  return (
                    <SelectionBlock
                      flow={flow}
                      key={selection.productAK}
                      price={selectionPrice}
                      selection={selection}
                    />
                  );
                })}
                {addOns?.length > 0 && (
                  <Fragment>
                    {addOns.map(
                      (addOn) =>
                        addOn.quantity &&
                        addOn.statGroup &&
                        !COMP_TICKETS.includes(addOn.statGroup) && (
                          <Flex key={addOn.ak} sx={{ ...spaceBetween }}>
                            <Text
                              sx={{
                                fontWeight: 500,
                              }}
                            >
                              {addOn.quantity} {addOn.name || addOn.description}
                            </Text>
                            {flow !== Flow.REDEMPTION && (
                              <Text
                                data-testid="selection-block-item-price"
                                sx={{
                                  fontWeight: 500,
                                }}
                              >
                                {formatMoney(
                                  addOn.price * (addOn.quantity || 1),
                                )}
                              </Text>
                            )}
                          </Flex>
                        ),
                    )}
                  </Fragment>
                )}
              </Flex>
            );
          });
        })}
      </Flex>
      {!isOrderConfirmed && (
        <Flex
          sx={{
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              position: 'relative',
            }}
          >
            <Input
              disabled={isCouponLoading}
              labelSx={{
                display: 'flex',
                flexDirection: 'column-reverse',
              }}
              name="couponCode"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCouponCode(e.target.value);
              }}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  applyCouponHandler();
                }
              }}
              placeholder={translations.summary.promoCodePlaceholder}
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.40)',
                bg: 'white',
                p: ['10px'],
                fontSize: [1, 1, 1],
              }}
              text={translations.summary.promoCodeLabel}
              value={couponCode}
            />
            {couponCode.length ? (
              <Box
                onClick={applyCouponHandler}
                sx={{
                  'position': 'absolute',
                  'top': '5px',
                  'right': 0,
                  'padding': '10px',
                  'width': '36px',
                  'height': ['43px', '49px'],
                  'cursor': 'pointer',
                  'transition': 'opacity 0.2s',
                  '&:hover': {
                    opacity: 0.4,
                  },
                }}
              >
                <CalendarArrow style={{ width: '100%', height: '100%' }} />
              </Box>
            ) : null}
          </Box>
          {!!totals.totalDiscount &&
            Math.abs(parseFloat(totals.totalDiscount)) > 0 &&
            totals.couponCode && (
              <Text>
                {totals.couponCode} applied (-
                {formatMoney(totals.totalDiscount, undefined, true)})
              </Text>
            )}
          {couponError && <Text sx={{ color: 'red' }}>{couponError}</Text>}
        </Flex>
      )}

      <Flex
        sx={{
          flexDirection: 'column',
          rowGap: 3,
          pt: 3,
          mt: 4,
          borderTop: '1px solid #b2b2b2',
        }}
      >
        {flow !== Flow.REDEMPTION && (
          <Fragment>
            {Number(totals?.totalTax ?? 0) > 0 && (
              <Flex sx={{ ...spaceBetween }}>
                <Text variant="largeP">Taxes</Text>
                {totals?.totalTax ? (
                  <Text data-testid="selection-block-tax" variant="largeP">
                    +
                    {totals?.inclusiveTax
                      ? formatMoney(
                          parseFloat(totals?.totalTax) -
                            parseFloat(totals?.inclusiveTax),
                        )
                      : formatMoney(totals?.totalTax)}
                  </Text>
                ) : null}
              </Flex>
            )}

            {Number(totals?.fee ?? 0) > 0 && (
              <Flex sx={{ ...spaceBetween }}>
                {/* <Text variant="largeP">{t('processing_fee')}</Text> */}
                <Text variant="largeP">processing fee</Text>
                {totals?.fee ? (
                  <Text variant="largeP">
                    +{formatMoney(totals?.fee, undefined, true)}
                  </Text>
                ) : null}
              </Flex>
            )}

            {parseFloatToDollars(totals?.totalDiscount ?? '0') > 0 ? (
              <Flex sx={{ ...spaceBetween }}>
                <Text variant="largeP">promo</Text>
                <ThemeProvider theme={getThemeByName('Top of the Rock Olive')}>
                  {totals?.totalDiscount ? (
                    <Text sx={{ color: 'background' }} variant="largeP">
                      {formatMoney(totals?.totalDiscount)}
                    </Text>
                  ) : null}
                </ThemeProvider>
              </Flex>
            ) : null}

            {Number(totals?.totalWithTax ?? 0) > 0 && (
              <Fragment>
                <Divider />
                <Flex sx={{ ...spaceBetween }}>
                  <Text sx={{ fontWeight: 'medium' }} variant="largeP">
                    Total with tax
                  </Text>
                  {totals?.totalWithTax ? (
                    <Text
                      data-testid="selection-block-total-price-with-tax"
                      sx={{ fontWeight: 'medium' }}
                      variant="largeP"
                    >
                      {formatMoney(totals?.totalWithTax)}
                    </Text>
                  ) : null}
                </Flex>
              </Fragment>
            )}
          </Fragment>
        )}
      </Flex>
    </Box>
  );
}
